import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PaidMediaOverviewRevenueValidationPlatform } from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewPct,
} from "../../utils/paidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import { OVERVIEW_GRID_COLOR, overviewAxisProps } from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

interface RevenueValidationCardProps {
  platforms: PaidMediaOverviewRevenueValidationPlatform[];
  shopifyAvailable: boolean;
  currency: string | null;
  loading: boolean;
  campaignFilterActive?: boolean;
}

const RevenueValidationCard: FC<RevenueValidationCardProps> = ({
  platforms,
  shopifyAvailable,
  currency,
  loading,
  campaignFilterActive = false,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const chartData = (platforms ?? []).map(platform => ({
    platform: overviewPlatformLabel(t, platform?.platform),
    platformReported: platform?.platform_reported_revenue ?? null,
    shopifyGross: platform?.shopify_gross_revenue ?? null,
    shopifyNet: platform?.shopify_net_revenue ?? null,
  }));

  const renderChart = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!chartData.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        <BarChart data={chartData} margin={chartRtl.margin({ top: 8, right: 10, bottom: 5, left: 0 })}>
          <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
          <XAxis dataKey="platform" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
          <YAxis
            width={88}
            tickFormatter={value => formatOverviewCurrency(value, currency)}
            orientation={chartRtl.yAxisOrientation}
            {...overviewAxisProps}
          />
          <Tooltip
            wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
            content={
              <CustomChartTooltip
                formatTooltip={(value: number) => formatOverviewCurrency(value, currency)}
              />
            }
          />
          <Bar
            dataKey="platformReported"
            name={t("paidMediaOverview.attribution.seriesPlatformReported")}
            className="platform-reported-bar"
            fill="#8d4fd3"
            radius={[3, 3, 0, 0]}
          />
          {shopifyAvailable ? (
            <Bar
              dataKey="shopifyGross"
              name={t("paidMediaOverview.attribution.seriesShopifyGross")}
              className="Shopify-gross-bar"
              fill="#5abf8b"
              radius={[3, 3, 0, 0]}
            />
          ) : null}
          {shopifyAvailable ? (
            <Bar
              dataKey="shopifyNet"
              name={t("paidMediaOverview.attribution.seriesShopifyNet")}
              className="shopify-net-bar"
              fill="#94d0b5"
              radius={[3, 3, 0, 0]}
            />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <article className="paid-media-panel paid-media-overview__attribution-chart-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.attribution.validationTitle")}
            <OverviewMetricTip
              id="reportingVariance"
              label={t("paidMediaOverview.attribution.validationTitle")}
            />
          </h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.attribution.validationSubtitle")}
          </p>
        </div>
      </div>
      {shopifyAvailable && campaignFilterActive ? (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attribution.campaignFilterNotAppliedToShopify")}
        </p>
      ) : null}
      <div className="paid-media-overview__chart paid-media-overview__chart--sm paid-media-overview-platform-and-shopify">
        {renderChart()}
      </div>
      <div
        className="paid-media-overview__chart-legend-row"
        aria-label={t("paidMediaOverview.attribution.legendAria")}
      >
        <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--purple">
          <span
            className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--purple"
            aria-hidden
          />
          <span>{t("paidMediaOverview.attribution.platformReported")}</span>
        </div>
        {shopifyAvailable ? (
          <>
            <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--green">
              <span
                className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--green"
                aria-hidden
              />
              <span>{t("paidMediaOverview.attribution.shopifyGross")}</span>
            </div>
            <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--mint">
              <span
                className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--mint"
                aria-hidden
              />
              <span>{t("paidMediaOverview.attribution.shopifyNet")}</span>
            </div>
          </>
        ) : null}
      </div>
      {shopifyAvailable ? (
        <div className="paid-media-overview__variance-grid">
          {(platforms ?? []).map(platform => (
            <VarianceBox
              key={platform?.platform}
              title={overviewPlatformLabel(t, platform?.platform)}
              variance={platform?.reporting_variance ?? null}
              formattedVariance={platform?.formatted_reporting_variance ?? null}
              adjRoas={platform?.refund_adjusted_roas ?? null}
              formattedAdjRoas={platform?.formatted_refund_adjusted_roas ?? null}
            />
          ))}
        </div>
      ) : (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attribution.shopifyUnavailable")}
        </p>
      )}
    </article>
  );
};

const VarianceBox: FC<{
  title: string;
  variance: number | null;
  formattedVariance: string | null;
  adjRoas: number | null;
  formattedAdjRoas: string | null;
}> = ({ title, variance, formattedVariance, adjRoas, formattedAdjRoas }) => {
  const { t } = useTranslation();
  const hasVariance = variance !== null && Number.isFinite(variance);
  const positive = hasVariance && (variance as number) > 0;
  const varianceTone = !hasVariance
    ? ""
    : positive
      ? "paid-media-overview__text-warning"
      : "paid-media-overview__text-success";

  const varianceDisplay = formattedVariance ?? `${positive ? "+" : ""}${formatOverviewPct(variance)}`;

  return (
    <div className="paid-media-overview__variance-box">
      <div className="paid-media-overview__variance-box-title">{title}</div>
      <div className="paid-media-overview__variance-box-metrics">
        <div>
          <div className="paid-media-overview__variance-box-label">
            {t("paidMediaOverview.attribution.reportingVariance")}
            <OverviewMetricTip
              id="reportingVariance"
              label={t("paidMediaOverview.attribution.reportingVariance")}
            />
          </div>
          <div className={`paid-media-overview__variance-box-value ${varianceTone}`.trim()}>
            {varianceDisplay}
          </div>
        </div>
        <div>
          <div className="paid-media-overview__variance-box-label">
            {t("paidMediaOverview.attribution.refundAdjustedRoas")}
            <OverviewMetricTip
              id="refundAdjustedRoas"
              label={t("paidMediaOverview.attribution.refundAdjustedRoas")}
            />
          </div>
          <div className="paid-media-overview__variance-box-value">
            {formattedAdjRoas ?? formatOverviewMultiplier(adjRoas)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueValidationCard;
